import React from 'react';
import { 
  RouterProvider, 
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  Navigate
} from 'react-router-dom';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Layout } from './components/Layout';
import { MobileLayout } from './components/mobile/MobileLayout';
import { LoginPage } from './pages/LoginPage';
import { MobileLoginPage } from './pages/mobile/MobileLoginPage';
import Dashboard from './pages/Dashboard';
import { MobileDashboard } from './pages/mobile/MobileDashboard';
import { OpenRateReport } from './pages/OpenRateReport';
import { StudentsPage } from './pages/StudentsPage';
import { MobileStudentsPage } from './pages/mobile/MobileStudentsPage';
import { InteractionsReport } from './pages/InteractionsReport';
import { EmailOpensPage } from './pages/EmailOpensPage';
import { SettingsPage } from './pages/SettingsPage';
import { MobileSettingsPage } from './pages/mobile/MobileSettingsPage';
import { DocumentationPage } from './pages/DocumentationPage';
import { OAuthCallback } from './pages/OAuthCallback';
import { useFirebaseAuth } from './hooks/useFirebaseAuth';
import { useDeviceDetect } from './hooks/useDeviceDetect';
import { Toaster } from "@/components/ui/toaster";

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useFirebaseAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0B1A] flex items-center justify-center">
        <p className="text-white">Carregando...</p>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  const { isMobile } = useDeviceDetect();

  const router = createBrowserRouter(
    createRoutesFromElements(
      <Route errorElement={<ErrorBoundary />}>
        <Route path="/login" element={isMobile ? <MobileLoginPage /> : <LoginPage />} />
        <Route path="/oauth2callback" element={<OAuthCallback />} />
        <Route element={
          <ProtectedRoute>
            {isMobile ? <MobileLayout /> : <Layout />}
          </ProtectedRoute>
        }>
          <Route path="/" element={isMobile ? <MobileDashboard /> : <Dashboard />} />
          <Route path="/open-rate-report" element={<OpenRateReport />} />
          <Route path="/students" element={isMobile ? <MobileStudentsPage /> : <StudentsPage />} />
          <Route path="/interactions-report" element={<InteractionsReport />} />
          <Route path="/email-opens" element={<EmailOpensPage />} />
          <Route path="/settings" element={isMobile ? <MobileSettingsPage /> : <SettingsPage />} />
          <Route path="/documentation" element={<DocumentationPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    ),
    {
      future: {
        v7_normalizeFormMethod: true,
        v7_prependBasename: true
      }
    }
  );

  return (
    <ErrorBoundary>
      <RouterProvider router={router} />
      <Toaster />
    </ErrorBoundary>
  );
}